/**
 * @voltxampere/web — `<vxa-shortcut-overlay>` (Sprint 11).
 *
 * `?` modal listing every registered shortcut grouped by
 * category (`global` / `symbol`). Each row: key chord on the
 * left, locale-aware description on the right. Re-renders
 * when the locale flips so descriptions stay in sync.
 *
 * Backdrop click + Escape close (Escape via ShortcutManager).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement } from "lit/decorators.js";

import { shortcutOverlayOpen } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import { shortcuts, type Shortcut } from "../shortcuts/index.js";

@customElement("vxa-shortcut-overlay")
export class VxaShortcutOverlay extends LitElement {
  private readonly _open = new SignalController(this, shortcutOverlayOpen);
  private readonly _locale = new SignalController(this, currentLocale);

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: var(--z-modal);
      display: none;
      align-items: flex-start;
      justify-content: center;
      background: rgba(3, 5, 10, 0.7);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }

    :host([data-open]) {
      display: flex;
    }

    .panel {
      margin-top: 80px;
      width: min(640px, 92vw);
      max-height: 70vh;
      overflow-y: auto;
      background: var(--ink);
      border: 1px solid var(--ivory-wisp);
      box-shadow: 0 0 0 1px var(--brass-ghost);
    }

    .header {
      padding: var(--s-4) var(--s-5);
      border-bottom: 1px solid var(--ivory-wisp);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .heading {
      font-family: var(--font-display);
      font-size: var(--fs-h3);
      font-weight: 400;
      color: var(--ivory);
      letter-spacing: -0.01em;
    }

    .hint {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-faint);
      text-transform: uppercase;
    }

    .section {
      padding: var(--s-4) var(--s-5);
    }

    .section + .section {
      border-top: 1px solid var(--ivory-wisp);
    }

    .section-header {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.28em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: var(--s-3);
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: var(--s-2);
    }

    .row {
      display: grid;
      grid-template-columns: 96px 1fr;
      gap: var(--s-4);
      align-items: baseline;
    }

    .key {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      color: var(--brass);
      border: 1px solid var(--brass-faint);
      padding: 4px 8px;
      letter-spacing: 0.18em;
      text-align: center;
      justify-self: start;
      white-space: nowrap;
    }

    .description {
      font-family: var(--font-body);
      font-size: var(--fs-small);
      color: var(--ivory);
      line-height: 1.4;
    }

    .empty {
      padding: var(--s-7) var(--s-5);
      font-family: var(--font-body);
      font-style: italic;
      color: var(--ivory-faint);
      text-align: center;
    }
  `;

  override updated(): void {
    this.toggleAttribute("data-open", this._open.value);
  }

  private readonly _onBackdrop = (e: MouseEvent): void => {
    if (e.target === this) {
      shortcutOverlayOpen.value = false;
    }
  };

  private renderSection(label: string, items: readonly Shortcut[]) {
    if (items.length === 0) return nothing;
    return html`
      <div class="section">
        <div class="section-header">${label}</div>
        <div class="list">
          ${items.map(
            (sc) => html`
              <div class="row">
                <span class="key">${this.displayKey(sc.key)}</span>
                <span class="description">${sc.describe()}</span>
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }

  /**
   * Render the canonical key string ("Cmd+K", "Shift+P") with
   * pretty modifier glyphs for the badge ("⌘K", "⇧P").
   */
  private displayKey(canonical: string): string {
    return canonical
      .replace(/^Cmd\+/, "⌘")
      .replace(/Alt\+/, "⌥")
      .replace(/Shift\+/, "⇧");
  }

  override render() {
    if (!this._open.value) return nothing;

    const all = shortcuts.list();
    const globals = all.filter((s) => s.category === "global");
    const symbols = all.filter((s) => s.category === "symbol");

    const isEmpty = globals.length === 0 && symbols.length === 0;

    // Pass an array (rather than an inner template whose only
    // body is two holes) so Lit's ChildPart unrolls every
    // section in happy-dom as well as in real browsers.
    const body = isEmpty
      ? html`<div class="empty">${t("shortcuts.empty")}</div>`
      : [
          this.renderSection(t("shortcuts.section.global"), globals),
          this.renderSection(t("shortcuts.section.symbols"), symbols),
        ];

    return html`
      <div
        class="panel"
        @click=${(e: Event) => {
          e.stopPropagation();
        }}
      >
        <div class="header">
          <span class="heading">${t("shortcuts.title")}</span>
          <span class="hint">${t("shortcuts.hint")}</span>
        </div>
        ${body}
      </div>
    `;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("click", this._onBackdrop as EventListener);
  }

  override disconnectedCallback(): void {
    this.removeEventListener("click", this._onBackdrop as EventListener);
    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-shortcut-overlay": VxaShortcutOverlay;
  }
}
